import {
  convertEpochToDate,
  fixedDigitsAndFormatNumberWithCurrency,
} from '../../../../utils/utils'
import type { WithdrawTransaction } from '../../types/withdrawals.types'
import { getLogoUrl } from '../plan/FundCardLayout'
import { StyledAmount } from '../plan/StyledAmount'

function CapitalGainsLine({
  amount,
  label,
}: {
  amount: number
  label: string
}) {
  if (amount === 0) return null
  return (
    <StyledAmount amount={amount} showBar label={label} decimals={2} colorVariant="light" showSign={false} />
  )
}

function buildCapitalGainsAnnouncement(info: WithdrawTransaction['financialYearCapitalGains']): string {
  if (!info) return ''
  const parts: string[] = []
  const push = (amount: number, label: string) => {
    if (amount === 0) return
    parts.push(`${fixedDigitsAndFormatNumberWithCurrency(Math.abs(amount), 2)} ${label}`)
  }
  push(info.ltcg ?? 0, 'LTCG')
  push(info.stcg ?? 0, 'STCG')
  push(info.ltcl ?? 0, 'LTCL')
  push(info.stcl ?? 0, 'STCL')
  return parts.join(', ')
}

type TransactionCardProps = {
  transaction: WithdrawTransaction
  isLast: boolean
}

export function TransactionCard({ transaction, isLast }: TransactionCardProps) {
  const info = transaction?.financialYearCapitalGains
  const ltcg = info?.ltcg ?? 0
  const stcg = info?.stcg ?? 0
  const ltcl = info?.ltcl ?? 0
  const stcl = info?.stcl ?? 0
  const amount = fixedDigitsAndFormatNumberWithCurrency(Math.abs(transaction?.transactionAmount ?? 0), 2)
  const date = convertEpochToDate(transaction?.transactionDate ?? 0, '%_d %b %y')
  const sourceLabel =
    transaction?.transactionSource === 'EXTERNAL'
      ? 'External'
      : transaction?.transactionSource === 'POWERUP'
        ? 'PowerUp'
        : ''
  const gainsAnnouncement = buildCapitalGainsAnnouncement(info)
  const cardLabel = [
    sourceLabel,
    transaction?.fundName ?? '',
    amount,
    gainsAnnouncement,
    date,
  ]
    .filter(Boolean)
    .join(', ')

  return (
    <div
      aria-label={cardLabel}
      className="w-full flex gap-4 bg-white min-w-0 pl-5 pt-3"
    >
      <div aria-hidden='true' className="flex items-center justify-center shrink-0 mt-6 w-10 h-10">
        <img id='mutualFundLogo' src={getLogoUrl(transaction?.logo)} alt='' className='h-10 w-10' />
      </div>
      <div aria-hidden='true' className="flex flex-col flex-1 min-w-0">
        <div className={`flex gap-3 ${isLast ? 'border-b-0' : 'border-b border-[#F0F4F7] pb-6'}`}>

          <div className="flex-1 min-w-0">
            <div className={`flex flex-col w-full `}>
              <div className="flex items-center gap-[5px] pb-[5px]">
                {transaction?.transactionSource === 'EXTERNAL' && (
                  <>
                    <ExternalIcon />
                    <span className="text-[12px] font-[400] text-[#6A6E6E]">External</span>
                  </>
                )}
                {transaction?.transactionSource === 'POWERUP' && (
                  <>
                    <PowerUpIcon />
                    <span className="text-[12px] font-[400] text-[#6A6E6E]">PowerUp</span>
                  </>
                )}
              </div>
              <div className="text-[13px] font-[400] text-[#6A6E6E] truncate">
                {transaction?.fundName ?? ''}
              </div>
              <div className="text-[18px] font-[500] text-black">
                {amount}
              </div>
              <div className="flex flex-col gap-[2px] pt-[10px]">
                {ltcg !== 0 && <CapitalGainsLine amount={ltcg} label="LTCG" />}
                {stcg !== 0 && <CapitalGainsLine amount={stcg} label="STCG" />}
                {ltcl !== 0 && <CapitalGainsLine amount={ltcl} label="LTCL" />}
                {stcl !== 0 && <CapitalGainsLine amount={stcl} label="STCL" />}
              </div>
            </div>
          </div>

          <div className="text-[13px] font-[400] text-[#6A6E6E] shrink-0 self-start pr-5 mt-5">
            {date}
          </div>
        </div>
      </div>

    </div>
  )
}

export function ExternalIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="shrink-0" aria-hidden='true'>
      <path
        d="M2.99969 2.5H8.9997V8.5"
        stroke="#6A6E6E"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8.99951 2.5L1.99951 9.5"
        stroke="#6A6E6E"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function PowerUpIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="shrink-0" aria-hidden='true'>
      <path
        d="M9.10712 4.83461H5.99997V0.950684L0.950867 7.16497H4.4464V11.0489L9.10712 4.83461Z"
        stroke="#6A6E6E"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
